/**
 * FirmaPersonelListPage.jsx — Personel Listesi (firma portalı, kart görünümü)
 * Route: /Firma/Personel
 *
 * Ana Sayfa'daki (FirmaAnasayfaListPage.jsx) "Kurulum ve Söküm Kartı Durumu"
 * panelinin "Tüm Personeller" bağlantısının hedefi.
 *
 * 2026-10-13 (backlog #357, backend #356): sayfa artık BEŞ SEKMELİ ve tek uçtan beslenir —
 *   Tüm Personeller (alt sekmeler: Tümü · Fuar Kadrosu · Görevlendirilmemiş · Onay Bekleyen) · Daimî Personeller ·
 *   Geçici Personeller · İlişiği Kesilmişler · Tam Liste.
 * Liste, kartlar ve eylemlerin tamamı Components/Kisi/FirmaPersonelSekmeleri.jsx'te (denetmen tarafındaki
 * Firma Detay › Personel Listesi ile AYNI bileşen); bu dosya yalnızca firma portalına özgü kısmı taşır:
 *   · hangi firma (PortalContext — #403) ve hangi fuar (üst çubuk),
 *   · yazma düğmeleri PERSONEL modülünün seviyesine bağlı (arayüz setine göre salt okunur olabilir),
 *   · yabancı firmada belge kareleri yerine KVKK + kimlik / pasaport,
 *   · fuar-seviyesi kart erişim uyarısı (vergi no + fuarın istediği ek belgeler),
 *   · kişinin detayına (FirmaPersonelDetailPage.jsx) ve belge karesinin gittiği sayfaya yönlendirme.
 * Eski "Fuar Kadrosu" / "Daimî Liste" görünümleri kalktı: ilki Tüm Personeller'in alt sekmesi, ikincisi (firmanın TÜM
 * kadrosuydu) Tüm Personeller'in kendisi. Liste artık HER fuarda canlı — eski mock köprüsü (fuar 298/299 dışında sahte
 * kadro) bu sayfadan kalktı; köprü hâlâ FirmaPersonelDetailPage'de, yani 298/299 dışındaki bir fuarda "Detaylar" mock
 * detaya düşer (ayrı iş).
 *
 * 2026-10-19 (backlog #511): etkin arayüz seti Yabancı Katılımcı ise (YabanciKatilimciParcalari.yabanciKatilimciMi) sayfa SADE açılır —
 * üç sekme, alt sekme yok, GDPR + Pasaport kutuları; ayrıntı FirmaPersonelSekmeleri.jsx'in başında. Yabancı Dekoratör eski
 * "KVKK + kimlik / pasaport" görünümünü (`yabanci` prop'u) görmeye devam eder.
 * 2026-10-21: Yabancı Katılımcı sekmeleri All Users · Assigned Users · Ex-Employees ve satırı Deniz'in beyaz tahta çizimine göre yeniden yapıldı
 * (YabanciKatilimciKarti.jsx); satırdaki firma adı oturumdaki firmanın kendi adı (`portal.firma.firmaName`).
 *
 * Geçmiş notlar (kaldırılan görünümlere ait olanlar kısaltıldı):
 *   · 2026-07-24: Kart Alabilir/Alamaz durumu kartın sol üst köşesinde "ribbon".
 *   · 2026-08-28: dört kare "Muaf" rengiyle basılmaz; firmanın üç İSG belgesi bir muafiyet değil bir ŞART.
 *   · 2026-10-01 (#114/#113): Saka'nın alanda "eşlediği" kişiyi firma Onaylar ya da sebeple Reddeder.
 *   · 2026-10-06 (#300): /Firma/PersonelListem bu sayfaya taşındı (Personel Ekle burada).
 */
import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Alert } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import MainContext from "../../Components/Contexts/MainContext";
import { PersonelLegendButton } from "../../Components/Kisi/PersonelKartParcalari";
import FirmaPersonelSekmeleri, { kadroSatiri } from "../../Components/Kisi/FirmaPersonelSekmeleri";
import { FirmaRedNedenleriProvider } from "../../Components/Firma/firmaRedNedenleri";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { yabanciKatilimciMi } from "../../Components/Kisi/YabanciKatilimciParcalari";
import { sadePanelMi } from "../../services/sadePanel";
import YabanciKatilimciSahaDisiNotu from "../../Components/Firma/YabanciKatilimciSahaDisiNotu";
import { MODUL, SEVIYE } from "../../services/portalService";
import { getFirmaCardAccessStatus, cardAccessModeMeta } from "../../services/cardAccessService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

/**
 * Eski /Firma/PersonelListem adresi (yer imi) → bu sayfa. Eski taşeron bağlantılarının `?firmaId=` / `?fuarId=`
 * parametreleri artık TAŞINMAZ (#382): taşeronun listesi kalktı, parametrelerin bağlanacağı bir görünüm yok.
 * Route allRoutes.js'te. (#357'ye kadar `gorunum=liste` eklenip Daimî Liste açılırdı; o görünüm artık Tüm Personeller.)
 */
export function FirmaPersonelListemYonlendir() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.delete("firmaId");
  params.delete("fuarId");
  // WAS (until #357): if (!params.has("gorunum")) params.set("gorunum", "liste");
  params.delete("gorunum");
  const query = params.toString();
  return <Navigate to={query ? `/Firma/Personel?${query}` : "/Firma/Personel"} replace />;
}

export default function FirmaPersonelListPage() {
  const { t: tx } = useTranslation("portal");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedFuarId } = useContext(MainContext);
  // #403: firma id'si portal bağlamından; çözülene kadar null (hiçbir istek "null" firmaya gitmez).
  const portal = usePortal();
  const firmaId = portal?.firmaId ?? null;
  // Yazma düğmeleri (Personel Ekle, görevlendir, sonlandır, çalışan tipi, Onayla/Reddet) PERSONEL modülü WRITE ve üstüyse.
  const yazabilir = !portal || portal.enAzModul(MODUL.PERSONEL, SEVIYE.WRITE);
  // #320: yabancı firma — personelin kişisel belgesi yalnızca kimlik / pasaport (backend #314, YABANCI yolu).
  const yabanciFirma = Boolean(portal?.yabanci);
  // #511: etkin arayüz seti Yabancı Katılımcı → sade liste (3 sekme, alt sekme yok, GDPR + Pasaport kutuları).
  const yabanciKatilimci = yabanciKatilimciMi(portal);
  // #704: the sade LIST (3 tabs, the short row card) also for every other company while Sade panel modu is ON; its documents stay the
  // company's own (the backend's set for its nationality and role), only the real Yabancı Katılımcı gets the GDPR + Pasaport boxes.
  const sadePanel = sadePanelMi(portal);
  const [cardAccessStatus, setCardAccessStatus] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:personel.personelListesiFds"); }, []);

  // Backlog #40/#41 — firma seviyesi kart erişim durumu (vergi no + bu fuarın istediği ek belgeler).
  useEffect(() => {
    if (!selectedFuarId || !firmaId) return undefined;
    let ignore = false;
    getFirmaCardAccessStatus(firmaId, selectedFuarId)
      .then((data) => { if (!ignore) setCardAccessStatus(data); })
      .catch(() => { /* sessizce düşer — bu sayfanın asıl verisi liste, banner ikincil */ });
    return () => { ignore = true; };
  }, [firmaId, selectedFuarId]);

  // Kişinin detayı seçili fuarın kadrosunu okur: kadroda olmayan kişide sayfa boş kalırdı, bu yüzden bağlantı/düğme yok.
  const detayOf = (row) => (
    selectedFuarId && kadroSatiri(row)
      ? { href: `/Firma/Personel/${row.personId}`, onGit: () => navigate(`/Firma/Personel/${row.personId}`) }
      : null
  );

  // "Firma İSG Belgeleri" firma çapında TEK kayıt olduğu için Personel Detayı'nda değil FirmaKartPage.jsx'in Evraklar
  // sekmesinde yönetiliyor; diğerleri personel detayındaki ilgili BelgeCard'a odaklanır.
  const belgeGit = (row, docKey) => {
    if (docKey === "firmaBelgeleri") navigate("/Firma/FirmaKart?tab=evraklar");
    else if (selectedFuarId) navigate(`/Firma/Personel/${row.personId}?belge=${docKey}`);
  };

  // #622: a foreign company owes no tax number (#434) - its temporary-access hint does not say "the tax number has been entered".
  const erisimMeta = cardAccessStatus && cardAccessStatus.cardAccessMode !== "FULL"
    ? cardAccessModeMeta(cardAccessStatus.cardAccessMode, { yabanci: cardAccessStatus.taxNumberGerekli === false })
    : null;

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-start gap-2">
          <div className="flex-grow-1"><BreadCrumb title={tx("personel.personelListesi")} pageTitle={tx("personel.firma")} /></div>
          {/* Backlog #107: kart üzerindeki kare/rozet/sayaçların ne anlama geldiğini açıklar. */}
          {/* #511: açıklama penceresi Yerli belge karelerini anlatır; Yabancı Katılımcı'nın iki kutusu kendini açıklar. */}
          {!yabanciKatilimci && <PersonelLegendButton portal />}
        </div>

        {/* #558: Yabancı Katılımcı'ya "fuar saatleri dışında alanda kimse yoksa personel girmeniz gerekmez" (başka setlerde render etmez). */}
        <YabanciKatilimciSahaDisiNotu />

        {portal?.hata && !firmaId && <Alert color="danger">{portal.hata}</Alert>}

        {/* Backlog #40/#41 — firma bu fuarda vergi no/ek belge eksikse en üstte, listeden önce görünür olmalı:
            "Kart Alabilir" rozeti tek başına yeterli değil, firma seviyesinde de bir engel olabilir. */}
        {erisimMeta && (
          <Alert color={erisimMeta.color} className="small">
            <span className="fw-semibold">{erisimMeta.label}:</span>{" "}
            {erisimMeta.hint}{" "}
            <Link to="/Firma/FirmaKart?tab=evraklar">{tx("common:duzenle")}</Link>
          </Alert>
        )}

        {firmaId && (
          <div data-tour="personel-liste">
            {/* #737: the reasons behind the rows' rejected company-document squares (read once, only if one is rejected). */}
            <FirmaRedNedenleriProvider firmaId={firmaId} fuarId={Number(selectedFuarId) || null}>
              <FirmaPersonelSekmeleri
                firmaId={firmaId}
                fuarId={Number(selectedFuarId) || null}
                taraf="firma"
                yazabilir={yazabilir}
                yabanci={yabanciFirma}
                yabanciKatilimci={yabanciKatilimci}
                sadePanel={sadePanel}
                firmaGorunenAdi={portal?.firma?.firmaName ?? null}
                baslangicSekme={searchParams.get("sekme") || undefined}
                baslangicAltSekme={searchParams.get("alt") || undefined}
                detayOf={detayOf}
                belgeGit={belgeGit}
              />
            </FirmaRedNedenleriProvider>
          </div>
        )}
      </Container>
    </div>
  );
}
